import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import _ from 'lodash';

import { openModalAction } from '../../../actions/modal';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { DATE_FORMAT_MONTH_NAME_2, DATE_FORMAT_YEAR_MONTH_DAY, TIME_HOUR_MINUTE_SECONDS_FORMAT_12_UPPERCASE } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import {
  downloadFileOnly,
  downloadXlsxFile,
  headerWithTooltip,
  renderNotes,
  timeHeaderWithTimezone,
  toolTipForTable,
} from '../../../utils';
import './patient.scss';
import { actions } from './redux/actions';
import { UPDATE_TIMER_TASK, ADD_TIMER_TASK, TASK_HISTORY_FILTER_OPTIONS_RESULT } from './redux/constants';
import { getProgramDisplayName, getProgramsFromPatient } from '../../../utils/cmsPrograms';
import Select from '../../../components/Select';
import Multiselect from '../../../components/Select/Multiselect';
import { getGlobalPageSize, hydrateTableRequestFromUrl } from '../../../utils/tableUtils';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { getTimerPrograms, getRetroactiveAddPrograms } from '../../../utils/worklogPrograms';

const EMPTY_FILTERS = { taskMetadataIds: [], enrollmentUuids: [], doctorUuids: [] };
const EMPTY_FILTER_OPTIONS = { tasks: [], enrollments: [], doctors: [] };

function TaskHistory({
  patientId,
  patientDetails,
  enrollmentId,
  taskHistoryList,
  taskList,
  endTaskData,
  addTimerTaskData,
  isLoading,
  pagination,
  globalPageSize,
  getTaskHistoryList,
  getTaskHistoryFilterOptions,
  showNotification,
  downloadTaskHistory,
  downloadTaskHistoryXlsx,
  onEditTask,
  onAddTask,
  openConfirmModal,
  getPatientBillableTime,
}) {
  const pageRequestRef = useRef({
    offset: 0,
    search: '',
    patientId,
    limit: globalPageSize,
  });
  const hasLoadedOnceRef = useRef(false);

  const preActivationWorklogsEnabled = patientDetails?.patientEnrollment?.preActivationWorklogsEnabled === true;
  const programs = React.useMemo(() => getProgramsFromPatient(patientDetails), [patientDetails]);
  const timerPrograms = React.useMemo(
    () => getTimerPrograms(programs, preActivationWorklogsEnabled),
    [programs, preActivationWorklogsEnabled],
  );
  // Enrollments the "Add Task" modal may target: active worklog enrollments that
  // already reached the "at least enrolled" state (RXC-1603). Wider than
  // timerPrograms - the pre-activation org flag does not gate retroactive adds.
  const retroactiveAddPrograms = React.useMemo(
    () => getRetroactiveAddPrograms(programs),
    [programs],
  );

  // Task, program and doctor filters, applied on top of the page-level enrollment selection.
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);

  const loadFilterOptions = useCallback(() => {
    getTaskHistoryFilterOptions({ patientId, enrollmentId }).then(resp => {
      if (resp?.type === TASK_HISTORY_FILTER_OPTIONS_RESULT) {
        setFilterOptions(resp.response);
      } else {
        showNotification(
          resp.response?.data?.standard_error?.message
            || resp.response?.data?.error?.message
            || Strings.capPatient.filterOptionsLoadFailed,
          5000,
          true,
        );
      }
    });
  }, [patientId, enrollmentId, getTaskHistoryFilterOptions, showNotification]);

  useEffect(() => {
    const nextRequest = {
      offset: 0,
      search: '',
      patientId,
      limit: globalPageSize,
      enrollmentId,
      ...EMPTY_FILTERS,
    };

    if (!hasLoadedOnceRef.current) {
      pageRequestRef.current = hydrateTableRequestFromUrl(nextRequest, globalPageSize);
      hasLoadedOnceRef.current = true;
    } else {
      pageRequestRef.current = nextRequest;
    }

    setSelectedFilters(EMPTY_FILTERS);
    getTaskHistoryList(pageRequestRef.current);
  }, [patientId, globalPageSize, getTaskHistoryList, enrollmentId]);

  useEffect(() => {
    setFilterOptions(EMPTY_FILTER_OPTIONS);
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Tasks were added, changed or removed: the list and the filter options may have changed.
  const refreshTaskHistory = useCallback(() => {
    getTaskHistoryList(pageRequestRef.current);
    loadFilterOptions();
  }, [getTaskHistoryList, loadFilterOptions]);

  // The timer stopped or added a task: reload with this page's own filters, paging and sorting.
  const lastTimerResultsRef = useRef({ endTaskData, addTimerTaskData });
  useEffect(() => {
    const last = lastTimerResultsRef.current;
    if (endTaskData === last.endTaskData && addTimerTaskData === last.addTimerTaskData) return;
    lastTimerResultsRef.current = { endTaskData, addTimerTaskData };
    refreshTaskHistory();
  }, [endTaskData, addTimerTaskData, refreshTaskHistory]);

  const onFilterChange = useCallback((key, values) => {
    setSelectedFilters(filters => ({ ...filters, [key]: values }));
    pageRequestRef.current[key] = values;
    pageRequestRef.current.offset = 0;
    getTaskHistoryList(pageRequestRef.current);
  }, [getTaskHistoryList]);



  const onDownload = useCallback((format) => {
    const downloadRequest = { patientId, ...selectedFilters };
    const fileName = `TaskHistory_${patientDetails?.patientName || patientId}`;

    if (format === 'xlsx') {
      downloadTaskHistoryXlsx(downloadRequest).then(req => {
        downloadXlsxFile(req, fileName);
      });
    } else {
      downloadTaskHistory(downloadRequest).then(req => {
        downloadFileOnly(req, fileName);
      });
    }
  }, [patientId, patientDetails?.patientName, downloadTaskHistoryXlsx, downloadTaskHistory, selectedFilters]);

  const onOffsetChange = useCallback((offset, limit) => {
    pageRequestRef.current.offset = offset;
    if (limit) pageRequestRef.current.limit = limit;
    getTaskHistoryList(pageRequestRef.current);
  }, [getTaskHistoryList]);

  const onSortingChange = useCallback((sorting) => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequestRef.current.sortColumn && sortType === pageRequestRef.current.sortType) {
      return;
    }
    pageRequestRef.current.offset = 0;
    pageRequestRef.current.sortColumn = sortColumn;
    pageRequestRef.current.sortType = sortType;
    getTaskHistoryList(pageRequestRef.current);
  }, [getTaskHistoryList]);

  const handleEditTask = useCallback((task, readOnly = false) => {
    onEditTask(patientId, task, taskList, timerPrograms, readOnly, () => {
      refreshTaskHistory();
      getPatientBillableTime(patientId);
    });
  }, [patientDetails, taskList, onEditTask, refreshTaskHistory, getPatientBillableTime]);

  const handleAddTask = useCallback(() => {
    onAddTask(patientId, taskList, retroactiveAddPrograms,
      enrollmentId && retroactiveAddPrograms.some(p => p.enrollmentId === enrollmentId) ? enrollmentId : undefined,
      () => {
      refreshTaskHistory();
      getPatientBillableTime(patientId);
    });
  }, [patientDetails, taskList, onAddTask, refreshTaskHistory, getPatientBillableTime, enrollmentId, retroactiveAddPrograms]);

  const onDeleteTaskHistory = useCallback(task => {
    const deleteRequest = {
      uuid: task.uuid,
    };
    const data = {
      title: (
        <span>
          {Strings.deleteTaksHistoryWarning} <b>{task.taskName}</b>?
        </span>
      ),

      onConfirmAction: actions.deleteTimerTask(deleteRequest),
      confirmPostAction: () => {
        refreshTaskHistory();
        getPatientBillableTime(patientId);
      },
      onCancelAction: null,
    };
    openConfirmModal(data);
  }, [patientId, openConfirmModal, refreshTaskHistory]);

  const getEnrollmentLabel = useCallback((enrollmentUuid, program, subprogram) => {
    if (!enrollmentUuid || !patientDetails?.patientEnrollment?.enrollments) {
      return '-';
    }
    const enrollment = patientDetails.patientEnrollment.enrollments[enrollmentUuid];
    if (!enrollment) {
      return `${getProgramDisplayName(program, subprogram)} (${Strings.ended})`;
    }
    return getProgramDisplayName(enrollment.program, enrollment.subprogram);
  }, [patientDetails?.patientEnrollment?.enrollments]);

  const getProgram = useCallback(
    task => getEnrollmentLabel(task.enrollmentUuid, task.program, task.subprogram),
    [getEnrollmentLabel],
  );

  const columns = useMemo(() => [
    columnHelper.accessor('taskName', {
      header: Strings.capPatient.taskName,
      enableSorting: false,
      size: 400,
    }),
    columnHelper.accessor('notes', {
      header: Strings.capPatient.notes,
      cell: info => renderNotes(info.getValue()),
      enableSorting: false,
    }),
    columnHelper.accessor('enrollmentUuid', {
      header: Strings.program,
      cell: info => getProgram(info.row.original),
      enableSorting: false,
    }),
    columnHelper.accessor('doctorName', {
      header: Strings.doctor,
      cell: info => info.getValue() || '-',
      enableSorting: false,
    }),
    columnHelper.accessor('taskTime', {
      id: 'taskTime',
      header: Strings.capPatient.taskTime,
      cell: info => info.getValue() ? moment.utc(info.getValue() * 1000).format('HH:mm:ss') : '-',
      accessorFn: row => {
        const evidenceItems = row.evidenceItems;
        let time = 0;
        if (evidenceItems?.length > 0) {
          evidenceItems.forEach(e => {
            // No-duration tasks (e.g. setup/education calls) have no end time.
            time += e.endTime ? e.endTime - e.startTime : 0;
          });
          return time;
        }
        return 0;
      },
      enableSorting: false,
    }),
    columnHelper.accessor('startTime', {
      id: 'startTime',
      header: () => timeHeaderWithTimezone(Strings.capPatient.startTime),
      cell: info => info.getValue()
        ? moment(info.getValue() * 1000).format(TIME_HOUR_MINUTE_SECONDS_FORMAT_12_UPPERCASE)
        : '-',
      accessorFn: row => {
        const evidenceItems = row.evidenceItems;
        if (evidenceItems?.length > 0) {
          return evidenceItems[0].startTime;
        }
        return null;
      },
      enableSorting: false,
    }),

    columnHelper.accessor('endTime', {
      id: 'endTime',
      header: () => timeHeaderWithTimezone(Strings.capPatient.endTime),
      cell: info => {
        let stoppedReasonEmoji = "⏹️";
        switch (info.row.original.stopReason) {
          case "active":
            stoppedReasonEmoji = "▶️"; // Timer is currently running
            break;
          case "manual":
            stoppedReasonEmoji = "⏹️";
            break;
          case "inactivity":
            stoppedReasonEmoji = "⌛"; // Timer was auto-stopped due to inactivity 
            break;
          case "interrupted":
            stoppedReasonEmoji = "⚠️"; // Timer session was interrupted
            break;
          default:
            stoppedReasonEmoji = "⏹️"; // Default to manual stop if reason is unknown
        }
        return info.getValue()
          ? toolTipForTable(`${moment(info.getValue() * 1000).format(TIME_HOUR_MINUTE_SECONDS_FORMAT_12_UPPERCASE)} ${stoppedReasonEmoji}`, Strings.timerStoppedReasons[info.row.original.stopReason] || 'Timer stopped')
          : '-';
      },
      accessorFn: row => {
        const evidenceItems = row.evidenceItems;
        if (evidenceItems?.length > 0) {
          return evidenceItems[evidenceItems.length - 1].endTime;
        }
        return null;
      },
      enableSorting: false,
    }),
    columnHelper.accessor('workDay', {
      id: 'date',
      header: () => headerWithTooltip(Strings.capPatient.date, Strings.billingDayTooltip),
      cell: info => {
        const workDay = info.getValue();
        return workDay
          ? moment(workDay, DATE_FORMAT_YEAR_MONTH_DAY).format(DATE_FORMAT_MONTH_NAME_2)
          : '-';
      },
      enableSorting: true,
    }),
    columnHelper.accessor('createdBeforeEnrollmentActivation', {
      header: Strings.recordedBeforeActivation,
      cell: info => toolTipForTable(
        info.getValue() ? Strings.yes : Strings.no,
        info.getValue() ? Strings.recordedBeforeActivationTooltip : Strings.recordedAfterActivationTooltip,
      ),
      enableSorting: false,
    }),
  ], [getProgram]);

  const downloadSelect = (
    <Select
      data={[
        { value: 'csv', label: Strings.downloadCsv },
        { value: 'xlsx', label: Strings.downloadXlsx },
      ]}
      onChange={option => onDownload(option.value)}
      placeholder={Strings.download}
      clearValueAfterSelection
      className="download-select"
    />
  );

  const filterSelects = [
    {
      key: 'taskMetadataIds',
      id: 'task-history-task-filter',
      label: Strings.capPatient.taskName,
      allText: Strings.capPatient.allTasks,
      selectedText: Strings.capPatient.filteredTasks,
      data: filterOptions.tasks.map(t => ({ value: t.id, label: t.name })),
    },
    {
      key: 'enrollmentUuids',
      id: 'task-history-program-filter',
      label: Strings.program,
      allText: Strings.capPatient.allPrograms,
      selectedText: Strings.capPatient.filteredPrograms,
      data: filterOptions.enrollments.map(e => ({ value: e.uuid, label: getEnrollmentLabel(e.uuid, e.program, e.subprogram) })),
    },
    {
      key: 'doctorUuids',
      id: 'task-history-doctor-filter',
      label: Strings.doctor,
      allText: Strings.capPatient.allDoctors,
      selectedText: Strings.capPatient.filteredDoctors,
      data: filterOptions.doctors.map(d => ({ value: d.uuid, label: d.name || '-' })),
    },
  ].map(filter => (
    <Multiselect
      key={filter.key}
      id={filter.id}
      label={filter.label}
      className="task-history-filter"
      data={filter.data}
      value={selectedFilters[filter.key]}
      placeholder={selectedFilters[filter.key].length > 0
        ? Strings.formatString(filter.selectedText, selectedFilters[filter.key].length)
        : filter.allText}
      onChange={options => onFilterChange(filter.key, options.map(o => o.value))}
      closeMenuOnSelect={false}
      searchByLabelOnly
    />
  ));

  const headerTitle = !isLoading && taskHistoryList && pagination
    ? Strings.formatString(
        Strings.showingXOf,
        taskHistoryList.length,
        pagination.totalRecords ? pagination.totalRecords : 0,
        Strings.billing.tasks,
      )
    : Strings.showingWait;

  return (
    <div className="patient-container noGap">
      <PageHeader
        isBlack
        left={headerTitle}
        right={(
          <>
            {filterSelects}
            <Button
              onClick={handleAddTask}
              title={Strings.capPatient.addTask}
              class={HEADER_BUTTON_DARK_BLUE}
              disabled={retroactiveAddPrograms.length === 0}
              tooltip={retroactiveAddPrograms.length === 0 ? Strings.capPatient.addTaskDisabledTooltip : undefined}
            />
          </>
        )}
      />
      <Table3
        className="patient-task-history-table"
        isLoading={isLoading}
        name="organizations"
        data={taskHistoryList || []}
        columns={columns}
        onRowClick={task => handleEditTask(task, true)}
        onSortingChange={onSortingChange}
        onOffsetChange={onOffsetChange}
        enableColumnFiltering
        pagination={
          pagination || {
            offset: 0,
            total: 0,
          }
        }
        footerExtra={downloadSelect}
        buttons={[
          {
            icon: 'delete',
            onClick: (id, task) => onDeleteTaskHistory(task),
            disabled: () => {
              return false;
            },
            text: Strings.delete,
          },
          {
            icon: 'edit',
            onClick: (id, task) => handleEditTask(task),
            disabled: () => {
              return false;
            },
            text: Strings.edit,
          },
        ]}
      />
    </div>
  );
};

TaskHistory.propTypes = {
  endTaskData: PropTypes.any,
  addTimerTaskData: PropTypes.any,
  getTaskHistoryList: PropTypes.func,
  getTaskHistoryFilterOptions: PropTypes.func,
  showNotification: PropTypes.func,
  downloadTaskHistory: PropTypes.func,
  downloadTaskHistoryXlsx: PropTypes.func,
  isLoading: PropTypes.any,
  onEditTask: PropTypes.func,
  onAddTask: PropTypes.func,
  openConfirmModal: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
    totalRecords: PropTypes.number,
  }),
  patientDetails: PropTypes.any,
  patientId: PropTypes.any,
  taskHistoryList: PropTypes.array,
  taskList: PropTypes.any,
  getPatientBillableTime: PropTypes.func,
  globalPageSize: PropTypes.number,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;

  return {
    taskHistoryList: patients?.taskHistoryList?.data,
    isLoading: patients?.isTaskHistoryListLoading,
    endTaskData: patients?.endTaskData,
    addTimerTaskData: patients?.addTimerTaskData,
    pagination: patients?.pagination,
    globalPageSize: getGlobalPageSize(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getTaskHistoryList: pageRequest => dispatch(actions.getTaskHistoryList(pageRequest)),
  getTaskHistoryFilterOptions: pageRequest => dispatch(actions.getTaskHistoryFilterOptions(pageRequest)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  downloadTaskHistory: pageRequest => dispatch(actions.downloadTaskHistory(pageRequest)),
  downloadTaskHistoryXlsx: pageRequest => dispatch(actions.downloadTaskHistoryXlsx(pageRequest)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  onEditTask: (patientId, task, taskList, programs, readOnly, successAction) =>
    dispatch(
      openModalAction('update-task', {
        action: actions.editTimerTask,
        successAction,
        actionType: UPDATE_TIMER_TASK,
        task,
        taskList,
        patientId,
        programs,
        readOnly,
      }),
    ),
  onAddTask: (patientId, taskList, programs, enrollmentId, successAction) =>
    dispatch(
      openModalAction('update-task', {
        action: actions.addTimerTask,
        successAction,
        actionType: ADD_TIMER_TASK,
        taskList,
        patientId,
        programs,
        enrollmentId,
      }),
    ),
  getPatientBillableTime: patientId => dispatch(actions.getPatientBillableTime(patientId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskHistory);
